import { useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldOff, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

// ── SkuIcon inline (pattern standard du projet) ──────────────────────────────
function SkuIcon({ children, color, size = 36 }: { children: ReactNode; color: string; size?: number }) {
  const r = Math.round(size * 0.28);
  return (
    <div className="shrink-0 flex items-center justify-center relative overflow-hidden"
      style={{
        width: size, height: size, borderRadius: r,
        background: `linear-gradient(150deg, ${color}30 0%, ${color}0d 100%)`,
        border: `1px solid ${color}40`,
        boxShadow: `0 4px 16px ${color}22, 0 1px 3px rgba(0,0,0,0.4), inset 0 1px 0 ${color}30, inset 0 -1px 0 rgba(0,0,0,0.3)`,
      }}
    >
      <div className="absolute inset-0 pointer-events-none"
        style={{ borderRadius: r, background: 'linear-gradient(180deg,rgba(255,255,255,0.07) 0%,transparent 50%)' }} />
      {children}
    </div>
  );
}

interface Finding {
  category?: string;
  title?: string;
  message?: string;
  severity: string;
  penalty?: number;
}

interface AcceptRiskModalProps {
  isOpen: boolean;
  finding: Finding | null;
  domain: string;
  onClose: () => void;
  onSubmit: (justification: string) => Promise<void>;
  isLoading?: boolean;
}

export default function AcceptRiskModal({
  isOpen, finding, domain, onClose, onSubmit, isLoading,
}: AcceptRiskModalProps) {
  const { lang } = useLanguage();
  const [justification, setJustification] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = justification.trim();
    if (trimmed.length < 10) {
      setError(lang === 'fr'
        ? 'La justification doit contenir au moins 10 caractères.'
        : 'Justification must be at least 10 characters.');
      return;
    }
    try {
      await onSubmit(trimmed);
      setJustification('');
      setError('');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (lang === 'fr' ? 'Erreur lors de l\'acceptation.' : 'Error accepting risk.'));
    }
  };

  const handleClose = () => {
    setJustification('');
    setError('');
    onClose();
  };

  const sevColor = finding?.severity === 'CRITICAL' ? '#ef4444'
    : finding?.severity === 'HIGH' ? '#f97316'
    : finding?.severity === 'MEDIUM' ? '#eab308'
    : finding?.severity === 'LOW' ? '#22c55e'
    : finding?.severity === 'INFO' ? '#3b82f6'
    : '#6b7280';

  return (
    <AnimatePresence>
      {isOpen && finding && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="sku-panel w-full max-w-md p-6 rounded-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <SkuIcon color="#f59e0b" size={36}>
                  <ShieldOff size={18} className="text-amber-600" />
                </SkuIcon>
                <h2 className="text-lg font-bold text-[var(--color-ink)]">
                  {lang === 'fr' ? 'Accepter ce risque' : 'Accept this risk'}
                </h2>
              </div>
              <button onClick={handleClose} className="text-[var(--color-muted)] hover:text-[var(--color-body)] transition-colors p-1">
                <X size={18} />
              </button>
            </div>

            {/* Finding summary */}
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-inset)] p-3 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: `${sevColor}20`, color: sevColor }}>
                  {finding.severity}
                </span>
                {finding.penalty != null && (
                  <span className="text-[10px] text-[var(--color-muted)] font-mono">-{finding.penalty} pts</span>
                )}
              </div>
              <p className="text-sm text-[var(--color-ink)] font-medium">{finding.title || finding.message}</p>
              <p className="text-xs text-[var(--color-muted)] mt-0.5">{finding.category} &middot; {domain}</p>
            </div>

            {/* Info */}
            <p className="text-xs text-[var(--color-muted)] mb-3 leading-relaxed">
              {lang === 'fr'
                ? 'Ce finding ne sera plus comptabilisé dans votre score de sécurité lors des prochains scans. Il restera visible et documenté dans vos rapports PDF.'
                : 'This finding will no longer count towards your security score on future scans. It will remain visible and documented in your PDF reports.'}
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div>
                <label className="block text-xs font-semibold text-[var(--color-body)] mb-1.5">
                  {lang === 'fr' ? 'Justification *' : 'Justification *'}
                </label>
                <textarea
                  value={justification}
                  onChange={e => { setJustification(e.target.value); setError(''); }}
                  placeholder={lang === 'fr'
                    ? 'Ex: Port SSH nécessaire pour l\'administration du serveur, accès restreint par firewall.'
                    : 'Ex: SSH port required for server admin, access restricted by firewall.'}
                  className="sku-inset w-full px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-faint)] resize-none"
                  rows={3}
                  autoFocus
                />
              </div>

              {error && (
                <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={handleClose} disabled={isLoading}
                  className="sku-btn-ghost flex-1 py-2 text-sm rounded-xl disabled:opacity-50">
                  {lang === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button type="submit" disabled={isLoading || justification.trim().length < 10}
                  className="flex-1 py-2 text-sm font-semibold rounded-xl transition-all disabled:opacity-40"
                  style={{
                    background: 'linear-gradient(135deg, rgba(251,191,36,0.15) 0%, rgba(245,158,11,0.10) 100%)',
                    border: '1px solid rgba(251,191,36,0.3)',
                    color: '#f59e0b',
                    boxShadow: '0 2px 8px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
                  }}>
                  {isLoading
                    ? (lang === 'fr' ? 'En cours\u2026' : 'Submitting\u2026')
                    : (lang === 'fr' ? 'Accepter le risque' : 'Accept risk')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
